import { useEffect, useState } from "react";
import axios from "axios";
import { X, Crown, ShieldCheck } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LIME = "#A4E832";
const CREAM = "#F5F2E6";
const PINK = "#FF2E9A";

/**
 * "Remove Ads" purchase modal — $1.99 for 30 days, PayPal-only.
 * Sign-in is required (backend enforces this on /paypal/create-order).
 * Founding-member VIPs see a "already ad-free" state instead.
 */
export default function NoAdsModal({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open || config) return;
    axios
      .get(`${API}/paypal/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setError("Payments are temporarily unavailable."));
  }, [open, config]);

  if (!open) return null;

  const alreadyAdFree = !!user?.ads_removed;
  const notSignedIn = !user;

  return (
    <div
      className="fixed inset-0 z-[2147483640] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      role="dialog"
      aria-modal="true"
      data-testid="noads-modal"
    >
      <div
        className="w-full max-w-md border-4 rounded-sm relative"
        style={{
          borderColor: LIME,
          background: "#0A0B08",
          color: CREAM,
          boxShadow: `10px 10px 0 0 ${PINK}`,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 p-2 text-white/70 hover:text-white"
          aria-label="Close"
          data-testid="noads-close"
        >
          <X size={18} strokeWidth={3} />
        </button>

        <div className="p-6 md:p-7">
          <div className="font-heading text-2xl md:text-3xl uppercase tracking-tight leading-none">
            KILL <span style={{ color: LIME }}>THE ADS</span>
          </div>
          <div
            className="font-body text-xs md:text-sm uppercase tracking-widest mt-2"
            style={{ color: "rgba(245,242,230,0.7)" }}
          >
            $1.99 · 30 days · popup gone · banners stay
          </div>

          {alreadyAdFree && (
            <div
              className="mt-5 p-4 border-2 rounded-sm flex items-start gap-2"
              style={{ borderColor: LIME, background: "rgba(164,232,50,0.08)" }}
              data-testid="noads-already"
            >
              <Crown size={16} strokeWidth={3} style={{ color: LIME }} className="mt-0.5" />
              <div>
                <div className="font-heading text-sm uppercase" style={{ color: LIME }}>
                  You're already ad-free
                </div>
                <div className="font-body text-xs uppercase tracking-widest mt-1 text-white/70">
                  {user.is_vip
                    ? `Founding member #${user.vip_rank} — pass is lifetime`
                    : "Pass is active"}
                </div>
              </div>
            </div>
          )}

          {!alreadyAdFree && notSignedIn && (
            <div
              className="mt-5 p-4 border-2 rounded-sm"
              style={{ borderColor: PINK, background: "rgba(255,46,154,0.08)" }}
              data-testid="noads-signin-required"
            >
              <div className="font-heading text-sm uppercase" style={{ color: PINK }}>
                Sign in first
              </div>
              <div className="font-body text-xs uppercase tracking-widest mt-1 text-white/70">
                Purchases follow your account across devices
              </div>
            </div>
          )}

          {!alreadyAdFree && !notSignedIn && (
            <>
              <ul className="mt-5 space-y-2 font-body text-xs md:text-sm text-white/85">
                <li className="flex items-center gap-2">
                  <ShieldCheck size={14} strokeWidth={3} style={{ color: LIME }} />
                  No more popup — full-screen never blocks you again
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck size={14} strokeWidth={3} style={{ color: LIME }} />
                  Renew any time · cancel by just not renewing
                </li>
                <li className="flex items-center gap-2">
                  <ShieldCheck size={14} strokeWidth={3} style={{ color: LIME }} />
                  Works on all your devices when signed in
                </li>
              </ul>

              {error && (
                <div
                  className="mt-4 p-3 border-2 font-body text-xs uppercase tracking-widest"
                  style={{ borderColor: PINK, color: PINK }}
                >
                  {error}
                </div>
              )}
              {success && (
                <div
                  className="mt-4 p-3 border-2 font-body text-xs uppercase tracking-widest"
                  style={{ borderColor: LIME, color: LIME }}
                  data-testid="noads-success"
                >
                  Paid · popup is gone. Enjoy the peace.
                </div>
              )}

              {config?.client_id && !success && (
                <div className="mt-5" data-testid="noads-paypal-mount">
                  <PayPalScriptProvider
                    options={{
                      "client-id": config.client_id,
                      currency: config.currency || "USD",
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      style={{
                        layout: "vertical",
                        color: "gold",
                        shape: "rect",
                        label: "paypal",
                        height: 45,
                      }}
                      createOrder={async () => {
                        setError("");
                        const r = await axios.post(
                          `${API}/paypal/create-order`,
                          { product_id: "no_ads_monthly" },
                          { withCredentials: true }
                        );
                        return r.data.order_id;
                      }}
                      onApprove={async (data) => {
                        try {
                          await axios.post(
                            `${API}/paypal/capture-order`,
                            {
                              order_id: data.orderID,
                              product_id: "no_ads_monthly",
                            },
                            { withCredentials: true }
                          );
                          setSuccess(true);
                          await refresh();
                        } catch (e) {
                          setError(
                            e?.response?.data?.detail ||
                              "Capture failed — PayPal will refund automatically."
                          );
                        }
                      }}
                      onError={() => setError("PayPal error — please try again.")}
                      onCancel={() => setError("Payment cancelled.")}
                    />
                  </PayPalScriptProvider>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
